import { useMemo, useState } from 'react';
import GemImage from '../components/GemImage.jsx';
import { findMonsterForEquipped, monsterDisplayName, elementLabel } from '../lib/astro.js';
import { summarizeEquippedStats } from '../lib/gemLogic.js';

function n(value, digits = 1) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function SetBonusText({ summary, lang }) {
  if (!summary?.activeSet) return null;
  const ko = lang !== 'en';
  const p = summary.setBonusPct || {};
  const f = summary.setBonusFlat || {};
  const labels = ko
    ? { hp: '체력', atk: '공격력', def: '방어력', rec: '회복력', crate: '치명확률', cdmg: '치명피해', res: '효과저항', acc: '효과적중' }
    : { hp: 'HP', atk: 'ATK', def: 'DEF', rec: 'REC', crate: 'Crit Rate', cdmg: 'Crit Damage', res: 'Resistance', acc: 'Accuracy' };
  const parts = [];
  for (const k of ['hp', 'atk', 'def', 'rec']) {
    if (Number(f[k] || 0)) parts.push(`${labels[k]} +${n(f[k], 0)}`);
    if (Number(p[k] || 0)) parts.push(`${labels[k]} +${n(p[k])}%`);
  }
  for (const k of ['crate', 'cdmg', 'res', 'acc']) if (Number(p[k] || 0)) parts.push(`${labels[k]} +${n(p[k])}%`);
  return <span>{parts.join(' · ')}</span>;
}

function LegacySummary({ summary, lang }) {
  const ko = lang !== 'en';
  const p = summary.totPct || {};
  const f = summary.totFlat || {};
  const inc = summary.increase || {};
  const final = summary.final || {};

  return (
    <div className="legacy-stat-summary-wrap">
      {summary.activeSet ? (
        <div className="set-bonus-breakdown">
          <b>{ko ? `✨ 발동된 세트 효과: ${summary.activeSet}` : `✨ Active set effect: ${summary.activeSet}`}</b>
          <SetBonusText summary={summary} lang={lang} />
        </div>
      ) : (
        <div className="muted legacy-no-set">{ko ? 'ℹ️ 발동된 세트 효과 없음 (동일 세트 보석 3개 필요)' : 'ℹ️ No active set effect (3 matching set gems required)'}</div>
      )}
      <div className="result-summary-title">{ko ? '📊 스탯 상승 종합 (세트 효과 포함)' : '📊 Total stat increase (set bonus included)'}</div>
      <div className="legacy-stat-summary">
        <div className="legacy-stat-column">
          <h4>{ko ? '[ 깡(+)옵션 합계 / 퍼센트(%) 합계 ]' : '[ Flat (+) total / Percent (%) total ]'}</h4>
          <div className="legacy-stat-row"><span>{ko ? '체력' : 'HP'}:</span><b>+{n(f.hp, 0)} / +{n(p.hp)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '공격력' : 'ATK'}:</span><b>+{n(f.atk, 0)} / +{n(p.atk)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '방어력' : 'DEF'}:</span><b>+{n(f.def, 0)} / +{n(p.def)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '회복력' : 'REC'}:</span><b>+{n(f.rec, 0)} / +{n(p.rec)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '치명확률 / 치명피해' : 'Crit Rate / Crit Damage'}:</span><b>+{n(p.crate)}% / +{n(p.cdmg)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '효과저항 / 효과적중' : 'Resistance / Accuracy'}:</span><b>+{n(p.res)}% / +{n(p.acc)}%</b></div>
        </div>
        <div className="legacy-stat-column">
          <h4>{ko ? '[ 최종 스탯 증가량 ]' : '[ Final stat increase ]'}</h4>
          <div className="legacy-stat-row"><span>{ko ? '최종 체력 증가량' : 'HP increase'}:</span><b>+{n(inc.hp, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 공격력 증가량' : 'ATK increase'}:</span><b>+{n(inc.atk, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 방어력 증가량' : 'DEF increase'}:</span><b>+{n(inc.def, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 회복력 증가량' : 'REC increase'}:</span><b>+{n(inc.rec, 0)}</b></div>
        </div>
        <div className="legacy-stat-column">
          <h4>{ko ? '[ 적용된 최종 스탯 ]' : '[ Applied final stats ]'}</h4>
          <div className="legacy-stat-row"><span>{ko ? '최종 체력' : 'Final HP'}:</span><b>{n(final.hp, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 공격력' : 'Final ATK'}:</span><b>{n(final.atk, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 방어력' : 'Final DEF'}:</span><b>{n(final.def, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '최종 회복력' : 'Final REC'}:</span><b>{n(final.rec, 0)}</b></div>
          <div className="legacy-stat-row"><span>{ko ? '치명확률 / 치명피해' : 'Crit Rate / Crit Damage'}:</span><b>{n(final.crate)}% / {n(final.cdmg)}%</b></div>
          <div className="legacy-stat-row"><span>{ko ? '효과저항 / 효과적중' : 'Resistance / Accuracy'}:</span><b>{n(final.res)}% / {n(final.acc)}%</b></div>
        </div>
      </div>
    </div>
  );
}

function EquippedGemCard({ gem, index, imageSource, lang }) {
  const ko = lang !== 'en';
  const star = gem.star ?? gem.stars ?? 6;
  const subs = (gem.sub_stats || []).filter((x) => x && typeof x === 'object');
  return (
    <div className="gem-card legacy-gem-card">
      <div className="legacy-slot-banner">{ko ? `슬롯 ${index + 1}` : `Slot ${index + 1}`}: {gem.name || (ko ? '보석' : 'Gem')} (#{gem.id})</div>
      <div className="legacy-gem-text">
        <div>• {ko ? '모양' : 'Shape'}: <b>{gem.shape || '-'}</b> | {ko ? '강화' : 'Enhance'}: <b>+{gem.enhancement ?? 0}{ko ? '강' : ''}</b> | {ko ? '별' : 'Star'}: <b>★{star}</b></div>
        <div>• {ko ? '세트' : 'Set'}: <b>{gem.set_option || '-'}</b></div>
        <div>• <b>{ko ? '주옵션' : 'Main'}</b>: {gem.main_stat?.name || '-'} {gem.main_stat?.value || ''}</div>
        <div className="legacy-substats"><b>{ko ? '부옵션' : 'Substats'}:</b>
          {subs.length ? subs.map((x, i) => <span key={i}>• {x.name} {x.value}</span>) : <span>• -</span>}
        </div>
      </div>
      <GemImage fileName={gem.file_name} imageSource={imageSource} alt={gem.name} />
    </div>
  );
}

export default function EquippedPage({ gems, astro, onChange, imageSource, lang, t }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const g of gems) {
      if (['보류', '휴지통'].includes(g.status) || !g.equipped_to) continue;
      if (!map.has(g.equipped_to)) map.set(g.equipped_to, []);
      map.get(g.equipped_to).push(g);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [gems]);

  const [selectedLabel, setSelectedLabel] = useState('');
  const activeLabel = groups.some(([label]) => label === selectedLabel) ? selectedLabel : groups[0]?.[0] || '';
  const activeGems = groups.find(([label]) => label === activeLabel)?.[1] || [];
  const monster = activeLabel ? findMonsterForEquipped(activeGems, astro, activeLabel) : null;
  const summary = activeGems.length ? summarizeEquippedStats(activeGems, monster) : null;
  const ko = lang !== 'en';

  function unequip(label) {
    onChange(gems.map((g) => g.equipped_to === label ? {
      ...g,
      equipped_to: null,
      equipped_monster_uid: null,
      equipped_variant_key: null,
      equipped_element: null,
      equipped_evolution: null,
      equipped_stat_type: null,
      equipped_monster_name_kor: null,
      equipped_instance_no: null,
    } : g));
    setSelectedLabel('');
  }

  if (!groups.length) return <section className="page-section"><h2>🔎 {t('equipped')}</h2><div className="empty-card">현재 장착된 보석이 없습니다.</div></section>;

  return (
    <section className="page-section">
      <div className="section-heading"><div><h2>🔎 {t('equipped')}</h2><p>{groups.length} Astromons</p></div></div>
      <div className="equipped-layout">
        <aside className="equipped-list card">
          {groups.map(([label, group]) => {
            const m = findMonsterForEquipped(group, astro, label);
            return <button key={label} className={`monster-list-button ${activeLabel === label ? 'active' : ''}`} onClick={() => setSelectedLabel(label)}>
              <span>🐾</span><span><b>{label}</b><small>{m ? `${elementLabel(m.element, lang)} · ${monsterDisplayName(m, lang)} · Evo ${m.evolution}` : 'DB match not found'}</small></span>
            </button>;
          })}
        </aside>

        <div className="equipped-detail">
          <div className="card">
            <div className="section-heading">
              <div>
                <h3>🐾 {activeLabel} {ko ? '장착 정보' : 'Equipped info'}</h3>
                {monster && <p>{ko ? '타입' : 'Type'}: <b>{monster.stat_type || '-'}</b> | {ko ? '기초 스탯' : 'Base stats'}: HP {n(summary?.base.hp, 0)} | ATK {n(summary?.base.atk, 0)} | DEF {n(summary?.base.def, 0)} | REC {n(summary?.base.rec, 0)}</p>}
              </div>
              <button className="danger" onClick={() => unequip(activeLabel)}>🧹 {t('unequip')}</button>
            </div>

            <h3 className="legacy-equipped-title">💎 {ko ? '장착 중인 보석 (최대 3개)' : 'Equipped gems (up to 3)'}</h3>
            {summary && <LegacySummary summary={summary} lang={lang} />}
          </div>

          <div className="result-gems">
            {[0, 1, 2].map((i) => {
              const g = activeGems[i];
              if (!g) return <div className="gem-card empty-slot" key={i}>⚪ {ko ? `슬롯 ${i + 1}: 장착된 보석 없음` : `Slot ${i + 1}: Empty`}</div>;
              return <EquippedGemCard gem={g} index={i} imageSource={imageSource} lang={lang} key={g.id} />;
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
